<template>
  <Header/>
  <LoginStatus />
  <div class="container is-max-desktop">
    <MessageError />

    <h1>Foretag en booking</h1>

    <template v-if="Array.isArray(global.user.upcommingBookings)">
    <template v-if="upcomingSlotsRemaining <= 2">
    <p>
      En husstand kan have op til <strong>{{ global.user.upcommingBookingsMax }} kommende bookinger</strong>. Din husstand har p.t. {{ global.user.upcommingBookings.length }}.
    </p>
    <div class="progress-wrapper">
      <progress class="progress" :class="upcomingSlotsRemaining < 1 ? 'is-danger' : upcomingSlotsRemaining == 1 ? 'is-warning' : 'is-success'" :value="global.user.upcommingBookings.length" :max="global.user.upcommingBookingsMax">
        {{ global.user.upcommingBookings.length }} / {{ global.user.upcommingBookingsMax }}
      </progress>
      <p class="progress-value">{{ global.user.upcommingBookings.length }} / {{ global.user.upcommingBookingsMax }}</p>
    </div>
    </template>
    <article class="message is-warning" v-if="upcomingSlotsRemaining < 1">
      <div class="message-body">
        <strong>Din husstands max er nået:</strong> Gå til <a href="/mine-bookinger">Mine bookinger</a>, og slet nogle af din husstands kommende bookinger, hvis du vil booke en ny nu.
      </div>
    </article>

    <div v-if="upcomingSlotsRemaining > 0" class="booking-form">
      <div class="booking-steps-bar">
        <ol class="booking-steps" aria-label="Bookingtrin">
          <li class="booking-step" :class="stepClass(1)">
            <span class="booking-step__num">1</span>
            <span>Tidsrum</span>
          </li>
          <li class="booking-step" :class="stepClass(2)">
            <span class="booking-step__num">2</span>
            <span>Køretøj</span>
          </li>
          <li class="booking-step" :class="stepClass(3)">
            <span class="booking-step__num">3</span>
            <span>Bekræft</span>
          </li>
        </ol>
      </div>

      <div class="form-section booking-period">
        <h2>Vælg tidsrum</h2>
        <div class="booking-period-fields">
          <div class="field">
            <div class="control">
              <label for="start">Afhentnings-tidspunkt</label>
              <input class="input" type="datetime-local" id="start" :min="booking.minStart"
                v-model="booking.start"
                @change="onDateFieldChange('booking.start')"
              >
            </div>
          </div>
          <div class="field">
            <div class="control">
              <label for="end">Afleverings-tidspunkt</label>
              <input class="input" type="datetime-local" id="end" :min="booking.minEnd"
                v-model="booking.end"
                @change="onDateFieldChange('booking.end')"
              >
            </div>
          </div>
        </div>

        <article v-if="Object.keys(booking.errorMessages).length" class="message is-danger">
          <div class="message-body">
            <ul>
              <li v-for="(error, propertyName) in booking.errorMessages" :key="propertyName">{{ error }}</li>
            </ul>
          </div>
        </article>
      </div>

      <div class="form-section available-vehicles">
        <h2>Vælg køretøj</h2>
        <ul class="vehicle-list">
          <li
            v-for="(vehicle, vehicleId) in booking.availableVehicles"
            :key="vehicleId"
            class="vehicle-option"
            :class="{
              'is-available': vehicle.available,
              'is-unavailable': !vehicle.available,
              'is-selected': booking.selectedVehicle == vehicleId && vehicle.available
            }"
          >
            <label class="vehicle-option__label">
              <input type="radio" name="vehicle"
                :disabled="!vehicle.available"
                :checked="booking.selectedVehicle == vehicleId && vehicle.available"
                :value="[vehicleId] + ['|'] + [vehicle.available ? 'available' : 'unavailable']"
                @change="onVehicleChange($event)"
              >
              <span>{{ vehicle.name }} ({{ vehicle.brand }})</span>
              <span v-if="!vehicle.available" class="vehicle-option__status">Ikke ledig</span>
            </label>
          </li>
        </ul>
      </div>

      <div v-if="booking.selectedVehicle != 0 && !Object.keys(booking.errorMessages).length" class="form-section finalize-booking">
        <h2>Færdiggør booking</h2>

        <article class="message is-warning reserve-notice">
          <div class="message-body">
            Din booking er reserveret. Bekræft inden for <strong>{{ booking.reserveTime }} sekunder</strong>.
          </div>
        </article>

        <article v-if="chargeGapWarning" class="message is-warning charge-gap-notice">
          <div class="message-body">
            Der er kort tid fra forrige bookings afslutning til din afhentning. Hvis elbilen har kørt langt, <strong>kan den mangle opladning</strong>. Kontakt gerne <strong>{{ chargeGapWarning.previousBookerName }} ({{ chargeGapWarning.previousAccountName }})</strong> og bed dem sætte bilen til lynladning.
          </div>
        </article>

        <div class="field">
          <div class="control">
            <label for="comment" class="sr-only">Evt. kommentar</label>
            <textarea class="textarea" id="comment" v-model="booking.comment" rows="3" placeholder="Evt. kommentar. Fx 'Jeg skal Aarhus og tager gerne andre med.'
  VIGTIGT: Ikke til beskeder til administrator!"></textarea>
          </div>
        </div>
        <div v-if="global.user.accessLvl == 1" class="field">
          <label class="checkbox">
            <input type="checkbox" v-model="booking.isAdminBooking">
            Administrator-booking (ingen beregning, husk beskrivelse)
          </label>
        </div>
        <div class="buttons booking-actions">
          <button class="button is-light" type="button" @click="bookingDelete(booking.id, booking.temp, calendarBackHref)">Fortryd</button>
          <button
            class="button is-success"
            type="button"
            :disabled="!booking.temp || booking.submitting"
            @click="bookingSubmit()"
          >
            {{ booking.temp ? 'Bekræft booking (' + booking.reserveTime + ')' : 'Reserverer…' }}
          </button>
        </div>
      </div>

      <article v-if="booking.reserveTimeEnded" class="message is-warning">
        <div class="message-header">
          <p>Reservation udløbet</p>
        </div>
        <div class="message-body">
          <p>Din reservation af køretøjet udløb.</p>
          <p>Vælg et køretøj igen, eller gå tilbage til kalenderen.</p>
        </div>
      </article>
    </div>
    </template>
  </div>

  <Footer/>
</template>

<script>
import ax from 'axios'
export const axios = ax

import { defineComponent } from 'vue'
import { useComposition } from '@/compositions/composition'

import Header from '@/components/Header.vue'
import Footer from '@/components/Footer.vue'
import LoginStatus from '@/components/LoginStatus.vue'
import MessageError from '@/components/MessageError.vue'

let minBookingTime = 60; // Minutes
let maxBookingTime = 60 * 24 * 10; // Minutes
let minBookingTimeSteps = 15; // Minutes
let bookingReserveTime = 60; // Seconds

export default defineComponent({
  setup() {
    const { global } = useComposition();

    return {
        global
    }
  },
  data() {
    return {
      nowIso: new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString(),
      fieldTypes: {
        start: 'text'
      },
      booking: {
        id: 0,
        temp: '',
        start: '',
        minStart: '',
        end: '',
        startEndDiff: minBookingTime,
        comment: '',
        isAdminBooking: false,
        availableVehicles: [],
        selectedVehicle: 0,
        reserveTime: bookingReserveTime,
        errorMessages: {},
        reserveTimeEnded: false,
        submitting: false,
        reserveTimer: null
      }
    }
  },
  components: {
    Header,
    Footer,
    LoginStatus,
    MessageError
  },
  created() {
      // Prefill from calendar date when not today; otherwise nearest :00/:15/:30/:45 from now
      const today = this.nowIso.substr(0, 10);
      const queryDate = typeof this.$route.query.date === 'string' ? this.$route.query.date : '';
      const useCalendarDate = /^\d{4}-\d{2}-\d{2}$/.test(queryDate) && queryDate > today;
      const startBase = useCalendarDate
        ? queryDate + 'T08:00'
        : this.nowIso.substr(0, 16);

      this.booking.start = this.ceilTime(startBase, minBookingTimeSteps);
      this.booking.minStart = this.ceilTime(this.nowIso.substr(0, 16), minBookingTimeSteps);

      this.booking.end = this.dateAdd(this.booking.start, this.booking.startEndDiff);
      this.booking.minEnd = this.booking.end;

      this.vehicleAvailability(this.booking.start, this.booking.end);
  },
  methods: {
    stepClass(step) {
      const current = this.currentStep;
      if (step < current) {
        return 'is-done';
      }
      if (step === current) {
        return 'is-active';
      }
      return '';
    },
    async bookingCreate() {
      // If there is already a temp booking (then user has chosen a new vehicle), then delete it first
      if (this.booking.temp != '') {
        await axios.get(process.env.VUE_APP_API_ROOT + '/booking.php?mode=delete&id=' + this.booking.id + '&temp=' + this.booking.temp);
        this.booking.temp = '';
        this.booking.id = 0;
      }

      const response = await axios.get(
        process.env.VUE_APP_API_ROOT + '/booking.php?mode=createTemp&start=' + encodeURIComponent(this.booking.start) + '&end=' + encodeURIComponent(this.booking.end) + '&vehicle=' + this.booking.selectedVehicle
      );

      if (response.data && response.data.hasOwnProperty('error')) {
        this.global.error.error = true;
        this.global.error.code = response.data.errorCode;
        this.global.error.alias = response.data.alias;
        this.booking.selectedVehicle = 0;
        return false;
      }

      if (!response.data || !response.data.id || !response.data.temp) {
        console.error('createTemp returnerede ugyldigt svar', response.data);
        this.booking.selectedVehicle = 0;
        return false;
      }

      this.booking.id = response.data.id;
      this.booking.temp = response.data.temp;
      return true;
    },
    bookingSubmit() {
      if (!this.booking.temp || this.booking.submitting) {
        return;
      }

      this.booking.submitting = true;
      axios.get(
        process.env.VUE_APP_API_ROOT + '/booking.php?mode=submit&id=' + this.booking.id +
        '&temp=' + encodeURIComponent(this.booking.temp) +
        '&comment=' + encodeURIComponent(this.booking.comment || '') +
        '&isAdminBooking=' + (this.booking.isAdminBooking ? '1' : '0')
      )
        .then(() => {
          if (this.booking.reserveTimer) {
            clearInterval(this.booking.reserveTimer);
            this.booking.reserveTimer = null;
          }
          const bookingId = this.booking.id;
          this.booking.temp = '';
          this.$router.push('/booking/' + bookingId + '?new=1');
        })
        .catch((error) => {
          console.error('Bekræft booking fejlede:', error);
          this.booking.submitting = false;
        });
    },
    bookingDelete(id, temp, goToLocation = '') {
      if (this.booking.reserveTimer) {
        clearInterval(this.booking.reserveTimer);
        this.booking.reserveTimer = null;
      }
      axios.get(process.env.VUE_APP_API_ROOT + '/booking.php?mode=delete&id=' + id + '&temp=' + encodeURIComponent(temp || ''))
        .then(() => {
          this.booking.temp = '';
          this.booking.id = 0;
          if (goToLocation != '') {
            this.$router.push(goToLocation);
          }
        });
    },
    onDateFieldChange(field) {
      switch(field) {
        case 'booking.start':
          this.booking.start = this.ceilTime(this.booking.start, minBookingTimeSteps);
          this.booking.end = this.dateAdd(this.booking.start, this.booking.startEndDiff);
          if(this.booking.start < this.nowIso) {
            this.booking.errorMessages.startInPast = 'Starttidspunkt kan ikke være i fortiden.';
          } else {
            delete this.booking.errorMessages.startInPast;
          }
          break;

        case 'booking.end':
          this.booking.end = this.ceilTime(this.booking.end, minBookingTimeSteps);
          this.booking.startEndDiff = this.dateDiff(this.booking.start, this.booking.end);
          break;
      }

      // Re-check available vehicles for every change of start or end times.
      this.vehicleAvailability(this.booking.start, this.booking.end);
      
      // Check if booking is too short
      // else if: Check if booking is too long
      if(this.booking.startEndDiff < minBookingTime) {
        this.booking.errorMessages.startEndDiff = this.booking.startEndDiff < 0 ? 'Sluttidspunkt må ikke være før starttidspunkt.' : 'Mindste booking er 1 time.';
      } else if(this.booking.startEndDiff > maxBookingTime) {
        this.booking.errorMessages.startEndDiff = 'Maksimal booking er 10 døgn.';
      } else {
        delete this.booking.errorMessages.startEndDiff;
      }
    },
    async onVehicleChange(e) {
      let data = e.target;
      let [vehicle, availability] = data.value.split('|');

      if(availability == 'available') {
        if (this.booking.reserveTimer) {
          clearInterval(this.booking.reserveTimer);
          this.booking.reserveTimer = null;
        }

        this.booking.selectedVehicle = parseInt(vehicle);
        this.booking.reserveTimeEnded = false;
        this.booking.reserveTime = bookingReserveTime;
        this.booking.temp = '';

        const created = await this.bookingCreate();
        if (!created) {
          return;
        }

        // Reservation timer – start first after temp booking exists
        var timeleft = bookingReserveTime;
        this.booking.reserveTimer = setInterval((scope) => {
          if(timeleft <= 0){
            clearInterval(scope.booking.reserveTimer);
            scope.booking.reserveTimer = null;

            // Delete temp booking, and inform user
            if(scope.booking.temp != '') {
              scope.bookingDelete(scope.booking.id, scope.booking.temp);
            }
            scope.booking.reserveTime = bookingReserveTime;
            scope.booking.selectedVehicle = 0;
            scope.booking.reserveTimeEnded = true;
          }
          scope.booking.reserveTime = timeleft;
          timeleft -= 1;
        }, 1000, this);
      }
    },
    ceilTime(dateString, minutesToRound, field = '') {
      let [dateStr, timeStr] = dateString.split('T');
      let [hoursStr, minutesStr] = timeStr.split(':');
      let minutes = parseInt(minutesStr, 10);
      let hours = parseInt(hoursStr, 10);
      let rounded = Math.ceil(minutes / minutesToRound) * minutesToRound;

      if (rounded >= 60) {
        rounded = 0;
        hours += 1;
      }
      if (hours >= 24) {
        hours -= 24;
        dateStr = this.dateAdd(dateStr + 'T12:00', 24 * 60).substr(0, 10);
      }

      let output = dateStr + 'T' + ('' + hours).padStart(2, '0') + ':' + ('' + rounded).padStart(2, '0');
      if (field != '') {
        this.booking.start = output;
      } else {
        return output;
      }
    },
    dateAdd(dateStr, minutes) {
      var dateIn = new Date(dateStr);
      var dateOut =  new Date(dateIn.getTime() + minutes*60000);

      return new Date(dateOut.getTime() - dateOut.getTimezoneOffset() * 60000).toISOString().substr(0, 16);
    }, 
    dateDiff(dateStartStr, dateEndStr) {
      let dateStart = new Date(dateStartStr);
      let dateEnd = new Date(dateEndStr);
      let diff = (dateEnd - dateStart);

      var diffDays = Math.floor(diff / 86400000); // days
      var diffHrs = Math.floor((diff % 86400000) / 3600000); // hours
      var diffMins = Math.round(((diff % 86400000) % 3600000) / 60000);

      return diffDays * 1440 + diffHrs * 60 + diffMins;
    },
    vehicleAvailability(start, end) {
      axios.get(process.env.VUE_APP_API_ROOT + '/vehicle-availability.php?start=' + start + '&end=' + end)
        .then(response => {
            this.booking.availableVehicles = response.data;
            if(this.booking.availableVehicles[this.booking.selectedVehicle] != undefined) {
              let isAvailable = this.booking.availableVehicles[this.booking.selectedVehicle].available;
              if(!isAvailable) {
                this.booking.selectedVehicle = 0;
              }
            }
      })
    }
  },
  computed: {
    currentRoute() {
        return this.$route;
    },
    upcomingSlotsRemaining() {
      const max = parseInt(this.global.user.upcommingBookingsMax, 10) || 0;
      const used = Array.isArray(this.global.user.upcommingBookings)
        ? this.global.user.upcommingBookings.length
        : 0;
      return max - used;
    },
    currentStep() {
      if (this.booking.selectedVehicle != 0 && !Object.keys(this.booking.errorMessages).length) {
        return 3;
      }
      if (!Object.keys(this.booking.errorMessages).length) {
        return 2;
      }
      return 1;
    },
    calendarBackHref() {
      const date = this.global.navigation.calendarDate || '';
      const view = ['day', 'week', 'month'].includes(this.global.navigation.calendarView)
        ? this.global.navigation.calendarView
        : 'day';
      const query = new URLSearchParams();
      if (date) {
        query.set('selectedDate', date);
      }
      query.set('view', view);
      return '/kalender?' + query.toString();
    },
    chargeGapWarning() {
      if (!this.booking.selectedVehicle) {
        return null;
      }
      const vehicle = this.booking.availableVehicles[this.booking.selectedVehicle];
      return (vehicle && vehicle.chargeGapWarning) ? vehicle.chargeGapWarning : null;
    }
  }
})
</script>
